import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal, Tag, message } from 'antd';
import { useCampusBranches } from '@hooks/tenant/useCampusBranches';
import { useTenantClasses } from '@hooks/tenant/useTenantClasses';
import { useTenantSubjects } from '@hooks/tenant/useTenantSubjects';
import { useTenantTeachersList } from '@hooks/tenant/useTenantTeachersList';
import { useCreateTenantClass } from '@hooks/tenant/useCreateTenantClass';
import { useCreateTenantSubject } from '@hooks/tenant/useCreateTenantSubject';
import { useUpdateTenantClass } from '@hooks/tenant/useUpdateTenantClass';
import { useUpdateTenantSubject } from '@hooks/tenant/useUpdateTenantSubject';
import { useDeleteTenantSubject } from '@hooks/tenant/useDeleteTenantSubject';
import { selectModalPopupContainer } from './selectModalPopupContainer';
import { formatPKR } from '@/utils/money';

/**
 * Classes + subjects CRUD state and handlers for the Classes & subjects tab.
 */
export function useTenantClassesSubjectsScreen({ tenantId }) {
    const { branches, loading: campusesLoading } = useCampusBranches({ tenantId });

    const campusPickList = useMemo(
        () => branches.filter((b) => (b.branchName || '').trim()),
        [branches]
    );
    const branchOptions = useMemo(
        () => campusPickList.map((b) => ({ value: b.id, label: b.branchName || b.id })),
        [campusPickList]
    );
    const branchNameById = useMemo(() => {
        const m = new Map();
        for (const b of branches) m.set(b.id, (b.branchName || '').trim() || b.id);
        return m;
    }, [branches]);

    const { rows: classes, loading: loadingClasses } = useTenantClasses({ tenantId });
    const { rows: subjects, loading: loadingSubjects } = useTenantSubjects({ tenantId });
    const { rows: teachers, loading: loadingTeachers } = useTenantTeachersList({ tenantId });

    const teacherSelectOptions = useMemo(
        () =>
            (teachers || []).map((t) => {
                const id = t.id || t.authUid;
                const label =
                    `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.displayName || t.email || t.teacherId || id || 'Teacher';
                return { value: id, label };
            }),
        [teachers]
    );

    const teacherNameById = useMemo(() => {
        const m = new Map();
        for (const t of teachers || []) {
            const id = t.id || t.authUid;
            if (!id) continue;
            const label =
                `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.displayName || t.email || t.teacherId || id;
            m.set(String(id), label);
        }
        return m;
    }, [teachers]);

    const createClassMutation = useCreateTenantClass({ tenantId });
    const createSubjectMutation = useCreateTenantSubject({ tenantId });
    const updateClassMutation = useUpdateTenantClass({ tenantId });
    const updateSubjectMutation = useUpdateTenantSubject({ tenantId });
    const deleteSubjectMutation = useDeleteTenantSubject({ tenantId });

    const [classModalOpen, setClassModalOpen] = useState(false);
    const [classDetailOpen, setClassDetailOpen] = useState(false);
    const [addSubjectOpen, setAddSubjectOpen] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editSubjectOpen, setEditSubjectOpen] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);

    const [classForm] = Form.useForm();
    const [addSubjectForm] = Form.useForm();
    const [detailForm] = Form.useForm();
    const [editSubjectForm] = Form.useForm();

    const [branchTableFilter, setBranchTableFilter] = useState('__all__');

    useEffect(() => {
        if (branchTableFilter !== '__all__' && !campusPickList.some((b) => b.id === branchTableFilter)) {
            setBranchTableFilter('__all__');
        }
    }, [campusPickList, branchTableFilter]);

    const filteredClasses = useMemo(() => {
        const list = classes || [];
        if (branchTableFilter === '__all__') return list;
        return list.filter((c) => (c.branchId || null) === branchTableFilter);
    }, [classes, branchTableFilter]);

    const classColumns = useMemo(
        () => [
            { title: 'Class', dataIndex: 'displayName', key: 'displayName', render: (v, r) => v || r.name || '—' },
            ...(campusPickList.length
                ? [
                    {
                        title: 'Campus',
                        dataIndex: 'branchId',
                        key: 'branchId',
                        render: (id) => (id ? branchNameById.get(id) || '—' : '—'),
                    },
                ]
                : []),
            { title: 'Grade', dataIndex: 'grade', key: 'grade', render: (v) => v || '—' },
            { title: 'Section', dataIndex: 'section', key: 'section', render: (v) => v || '—' },
            {
                title: 'Fees',
                dataIndex: 'fees',
                key: 'fees',
                width: 120,
                render: (v) => v !== undefined && v !== null ? formatPKR(v) : '—',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (v) => <Tag color={(v || 'active') === 'active' ? 'green' : 'default'}>{(v || 'active').toUpperCase()}</Tag>,
            },
        ],
        [campusPickList.length, branchNameById]
    );

    const classSubjects = useMemo(() => {
        if (!selectedClass?.id) return [];
        const cid = selectedClass.id;
        return (subjects || []).filter((s) => Array.isArray(s.classIds) && s.classIds.includes(cid));
    }, [subjects, selectedClass]);

    const openEditSubject = useCallback(
        (row) => {
            setSelectedSubject(row);
            editSubjectForm.setFieldsValue({
                name: row?.name || '',
                code: row?.code || '',
                status: (row?.status || 'active') === 'active',
                assignedTeacherId: row?.assignedTeacherId || undefined,
            });
            setEditSubjectOpen(true);
        },
        [editSubjectForm]
    );

    const confirmDeleteSubject = useCallback(
        (row) => {
            Modal.confirm({
                title: 'Delete subject?',
                content: `Are you sure you want to delete "${row?.name || 'this subject'}"?`,
                className: 'app-modal-confirm--themed',
                okText: 'Yes, delete',
                okType: 'danger',
                cancelText: 'No',
                onOk: async () => {
                    await deleteSubjectMutation.mutateAsync({ subjectId: row.id });
                    message.success('Subject deleted');
                },
            });
        },
        [deleteSubjectMutation]
    );

    const unassignTeacherFromSubject = useCallback(
        (row) => {
            const subjectName = row?.name || 'this subject';
            Modal.confirm({
                title: 'Unassign teacher?',
                content: `Remove the assigned teacher from “${subjectName}”?`,
                className: 'app-modal-confirm--themed',
                okText: 'Unassign',
                cancelText: 'Cancel',
                onOk: async () => {
                    await updateSubjectMutation.mutateAsync({
                        subjectId: row.id,
                        payload: { assignedTeacherId: null },
                    });
                    message.success('Teacher unassigned from subject');
                    if (selectedSubject?.id === row.id) {
                        setEditSubjectOpen(false);
                        setSelectedSubject(null);
                        editSubjectForm.resetFields();
                    }
                },
            });
        },
        [updateSubjectMutation, selectedSubject?.id, editSubjectForm]
    );

    const saveSubjectEdits = useCallback(async () => {
        try {
            const values = await editSubjectForm.validateFields();
            const subjectId = selectedSubject?.id;
            if (!subjectId) throw new Error('Missing subject');
            await updateSubjectMutation.mutateAsync({
                subjectId,
                payload: {
                    name: values.name,
                    code: values.code || null,
                    status: values.status ? 'active' : 'inactive',
                    assignedTeacherId: values.assignedTeacherId ?? null,
                },
            });
            message.success('Subject updated');
            setEditSubjectOpen(false);
            setSelectedSubject(null);
        } catch (e) {
            if (e?.message !== 'Validation failed') message.error(e?.message || 'Failed to update subject');
        }
    }, [editSubjectForm, selectedSubject?.id, updateSubjectMutation]);

    const openCreateClass = useCallback(() => {
        classForm.resetFields();
        classForm.setFieldsValue({
            status: 'active',
            ...(campusPickList.length === 1 ? { branchId: campusPickList[0].id } : {}),
        });
        setClassModalOpen(true);
    }, [classForm, campusPickList]);

    const openClassDetail = useCallback(
        (row) => {
            setSelectedClass(row);
            setIsEditing(false);
            detailForm.setFieldsValue({
                displayName: row?.displayName || '',
                grade: row?.grade || '',
                section: row?.section || '',
                name: row?.name || '',
                status: (row?.status || 'active') === 'active',
                branchId: row?.branchId || undefined,
                fees: row?.fees !== undefined && row?.fees !== null ? row.fees : undefined,
            });
            setClassDetailOpen(true);
        },
        [detailForm]
    );

    const openAddSubjectForClass = useCallback(() => {
        addSubjectForm.resetFields();
        setAddSubjectOpen(true);
    }, [addSubjectForm]);

    const submitCreateClass = useCallback(async () => {
        try {
            const values = await classForm.validateFields();
            const displayName = (values.displayName || `${values.grade || ''}${values.section ? ` - ${values.section}` : ''}`).trim();
            await createClassMutation.mutateAsync({
                payload: {
                    displayName: displayName || null,
                    name: values.name || null,
                    grade: values.grade || null,
                    section: values.section || null,
                    status: values.status || 'active',
                    fees: values.fees !== undefined && values.fees !== null ? Number(values.fees) : null,
                    ...(campusPickList.length ? { branchId: values.branchId || null } : { branchId: null }),
                },
            });
            message.success('Class created');
            setClassModalOpen(false);
        } catch (e) {
            if (e?.message !== 'Validation failed') message.error(e?.message || 'Failed to create class');
        }
    }, [classForm, campusPickList.length, createClassMutation]);

    const submitAddSubjectToClass = useCallback(async () => {
        try {
            const values = await addSubjectForm.validateFields();
            const classId = selectedClass?.id;
            if (!classId) throw new Error('Missing class');
            await createSubjectMutation.mutateAsync({
                payload: {
                    name: values.name,
                    code: values.code || null,
                    classIds: [classId],
                    status: values.status || 'active',
                    assignedTeacherId: values.assignedTeacherId ?? null,
                },
            });
            message.success('Subject added to class');
            setAddSubjectOpen(false);
        } catch (e) {
            if (e?.message !== 'Validation failed') message.error(e?.message || 'Failed to add subject');
        }
    }, [addSubjectForm, createSubjectMutation, selectedClass?.id]);

    const saveClassEdits = useCallback(async () => {
        try {
            const values = await detailForm.validateFields();
            const classId = selectedClass?.id;
            if (!classId) throw new Error('Missing class');
            const displayName =
                (values.displayName || `${values.grade || ''}${values.section ? ` - ${values.section}` : ''}`).trim() || null;
            await updateClassMutation.mutateAsync({
                classId,
                payload: {
                    displayName,
                    grade: values.grade || null,
                    section: values.section || null,
                    name: values.name || null,
                    status: values.status ? 'active' : 'inactive',
                    fees: values.fees !== undefined && values.fees !== null ? Number(values.fees) : null,
                    ...(campusPickList.length ? { branchId: values.branchId || null } : {}),
                },
            });
            message.success('Class updated');
            setIsEditing(false);
            setSelectedClass((prev) =>
                prev
                    ? {
                        ...prev,
                        displayName,
                        grade: values.grade || null,
                        section: values.section || null,
                        name: values.name || null,
                        status: values.status ? 'active' : 'inactive',
                        fees: values.fees !== undefined && values.fees !== null ? Number(values.fees) : null,
                        ...(campusPickList.length ? { branchId: values.branchId || null } : {}),
                    }
                    : prev
            );
        } catch (e) {
            if (e?.message !== 'Validation failed') message.error(e?.message || 'Failed to update class');
        }
    }, [campusPickList.length, detailForm, selectedClass?.id, updateClassMutation]);

    return {
        campusesLoading,
        campusPickList,
        branchOptions,
        branchNameById,
        filteredClasses,
        classes,
        loadingClasses,
        loadingSubjects,
        loadingTeachers,
        teacherSelectOptions,
        teacherNameById,
        classColumns,
        classSubjects,
        selectedClass,
        isEditing,
        setIsEditing,
        classModalOpen,
        setClassModalOpen,
        classDetailOpen,
        setClassDetailOpen,
        addSubjectOpen,
        setAddSubjectOpen,
        editSubjectOpen,
        setEditSubjectOpen,
        selectedSubject,
        setSelectedSubject,
        classForm,
        addSubjectForm,
        detailForm,
        editSubjectForm,
        branchTableFilter,
        setBranchTableFilter,
        createClassMutation,
        createSubjectMutation,
        updateClassMutation,
        updateSubjectMutation,
        openCreateClass,
        openClassDetail,
        openAddSubjectForClass,
        submitCreateClass,
        submitAddSubjectToClass,
        saveClassEdits,
        saveSubjectEdits,
        openEditSubject,
        confirmDeleteSubject,
        unassignTeacherFromSubject,
        selectModalPopupContainer,
    };
}
